@import '../../theme/global';

:host {
  --default-box-shadow: #{$box-shadow-base};
  @include button-reset;
  @include set-font;
  @include box-sizing;

  a {
    text-decoration: none;
  }
  box-sizing: border-box;

  display: block;
  font-family: $font-family;

  .snap-button[disabled] {
    background-color: $gray-100;
    color: $gray-400;
    opacity: 1;

    .snap-badge {
      background-color: $gray-400;
      color: $gray-100;
    }
  }

  .full-width {
    justify-content: center;
    width: 100%;
  }

  .snap-button {
    align-items: center;
    border: none;
    border-radius: 0.375rem;
    box-shadow: var(--default-box-shadow);
    cursor: pointer;
    display: inline-flex;
    font-weight: 500;
  }

  a[type='button'].snap-button {
    appearance: none;
  }

  ::slotted(snap-badge),
  snap-badge {
    margin-left: 0.5rem;

    --border-radius: 9999px;
    --padding-tiny-x: 1px;
    --width: 14px;
    --height: 14px;
    --display: flex;
    --snap-info-background: #fff !important;
  }

  .snap-button-secondary,
  .snap-button-tertiary {
    ::slotted(snap-badge),
    snap-badge {
      --snap-info-background: #{$gray-600} !important;
      --snap-info-text: #{$gray-100} !important;
    }
  }

  .snap-button,
  snap-button-group .snap-button-group-item {
    background-color: var(--default-background);
    background-image: var(--background-image);
    background-position: var(--background-position);
    background-repeat: var(--background-repeat);
    background-size: var(--background-size);
    border: 1px solid var(--default-border);
    color: var(--text-color);

    &:hover {
      background-color: var(--hover-background);
      border: 1px solid var(--default-hover-border);
    }

    &:focus-visible {
      background-color: var(--default-background);
      box-shadow: 0 0 0 2px $base-light, 0 0 0 4px $blue-500;
      outline: none;
    }

    &:active {
      background-color: var(--active-background);
      border: 1px solid var(--active-border);
      outline: 1px solid var(--active-border);
    }

    &--circular {
      border-radius: 9999px;
    }

    &-tertiary {
      &:focus {
        .snap-badge {
          background-color: $blue-500;
          color: $base-light;
        }
      }

      &:active {
        border: 1px solid var(--default-border);
        outline: none;

        .snap-badge {
          background-color: $base-light;
          color: $blue-600;
        }
      }

      .snap-badge {
        background-color: $blue-500;
        color: $base-light;
      }
    }

    &.xs {
      font-size: 0.75rem;
      height: 34px;
      line-height: 1rem;
      padding: 0 0.75rem;

      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0.375rem;
      }

      ::slotted(snap-badge),
      snap-badge {
        margin-left: 0.25rem;
      }

      &.right {
        ::slotted(snap-icon),
        snap-icon {
          margin-left: 0.625rem;
          margin-right: 0;
        }
      }

      &.icon-only {
        padding: 0.25rem;
      }
    }

    &.sm {
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0.375rem 0.875rem;

      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0.625rem;
      }

      ::slotted(snap-badge),
      snap-badge {
        margin-left: 0.25rem;
      }

      &.right {
        ::slotted(snap-icon),
        snap-icon {
          margin-left: 0.625rem;
          margin-right: 0;
        }
      }

      &.icon-only {
        padding: 0.375rem;
      }
    }

    &.md {
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0.5rem 1rem;

      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0.625rem;
      }

      ::slotted(snap-badge),
      snap-badge {
        margin-left: 0.25rem;
      }

      &.right {
        font-size: 0.875rem;
        line-height: 1.25rem;
        padding: 0.5rem 0.875rem;

        ::slotted(snap-icon),
        snap-icon {
          margin-left: 0.625rem;
          margin-right: 0;
        }
      }

      &.icon-only {
        padding: 0.5rem;
      }
    }

    &.lg {
      font-size: 1rem;
      line-height: 1.5rem;
      padding: 0.5rem 1rem;

      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0.875rem;
      }

      &.right snap-icon {
        margin-left: 0.875rem;
        margin-right: 0;
      }

      &.icon-only {
        padding: 0.5rem;
      }
    }

    &.xl {
      font-size: 1rem;
      line-height: 1.5rem;
      padding: 0.75rem 1.5625rem;

      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0.875rem;
      }

      &.right {
        ::slotted(snap-icon),
        snap-icon {
          margin-left: 0.875rem;
          margin-right: 0;
        }
      }

      &.icon-only {
        padding: 0.75rem;
      }
    }

    &.xxl {
      font-size: 1rem;
      line-height: 1.5rem;
      padding: 0.75rem 1.5625rem;

      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0.875rem;
      }

      &.right {
        ::slotted(snap-icon),
        snap-icon {
          margin-left: 0.875rem;
          margin-right: 0;
        }
      }

      &.icon-only {
        padding: 0.688rem;
      }
    }

    &.icon-only {
      ::slotted(snap-icon),
      snap-icon {
        margin-right: 0;
      }
    }

    &[disabled] {
      border: 1px solid $gray-300;
      cursor: none;
      pointer-events: none;

      ::slotted(snap-badge),
      snap-badge {
        --snap-info-background: #{$gray-400} !important;
        --snap-info-text: #{$gray-100} !important;
      }
    }


    &--rectangular {
      border: 0;
      border-radius: 0 0.5rem 0.5rem 0;
      box-shadow: none;

      &.icon-only.md {
        padding: 0.5rem 1rem;
      }

      &:hover,
      &:active,
      &[disabled]{
        border: 0;
      }

      snap-icon {
        color: $gray-600;
      }

      &.snap-button-primary {
        border: 1px solid $blue-900;

        &:hover {
          border-color: var(--default-background);
        }

        snap-icon {
          color: $base-light;
        }
      }

      &[disabled]{
        snap-icon {
          color: $gray-400;
        }
      }

      &.snap-button-secondary:active,
      &.snap-button-primary:active{
        border: 1px solid var(--active-border);
      }
    }
  }
}

:host(.snap-button-disabled) {
  pointer-events: none;
}
